import { CloudLightning, CloudRain, CloudSun } from 'lucide-react';

/**
 * EL TIEMPO DEL SERVIDOR — modelo único.
 *
 * <p>Aquí vive TODO lo que hay que saber para leer `mod_weather/main`: cuántas
 * casillas tiene un día, cómo se guarda cada una, qué hora es en Madrid y —lo
 * importante— **cómo se decide si un chubasco del 60% ocurre de verdad**. Lo
 * usan la rejilla del admin, el panel de «a quién le pilla el agua» y la tarjeta
 * del panel de Jartinet.</p>
 *
 * <p><b>Hay una copia mínima de esto en `functions/index.js`</b>
 * (`buildServerControl`), que es quien le dice al mod qué clima poner. Las dos
 * tienen que resolver IGUAL: si se separan, el juego haría una cosa y la web
 * diría otra. Lo que hay que mantener a la par es `SLOTS_DIA`, el formato de
 * celda y el `sorteo()`.</p>
 */

export type Weather = 'clear' | 'rain' | 'thunder';

export const WEATHER_ORDER: Weather[] = ['clear', 'rain', 'thunder'];

export const WEATHER_META: Record<Weather, { label: string; color: string; icon: any }> = {
  clear: { label: 'Despejado', color: '#fbbf24', icon: CloudSun },
  rain: { label: 'Lluvia', color: '#38bdf8', icon: CloudRain },
  thunder: { label: 'Tormenta', color: '#a78bfa', icon: CloudLightning },
};

/** Peso para elegir el tiempo "titular" de un rato: manda lo más gordo. */
export const WEATHER_PESO: Record<Weather, number> = { clear: 0, rain: 1, thunder: 2 };

/** Minutos que dura cada casilla del pronóstico. */
export const MINUTOS_SLOT = 15;
/** Casillas por día. **Era 24 (una por hora) hasta el 2026-09-02.** */
export const SLOTS_DIA = (24 * 60) / MINUTOS_SLOT; // 96
/** Casillas por hora (4). Es también el tamaño del bloque del sorteo, ver `sorteo`. */
export const SLOTS_HORA = 60 / MINUTOS_SLOT;

/**
 * Una casilla del pronóstico: qué tiempo está programado y **con qué
 * probabilidad de que pase de verdad** (0-100). Un `clear` no tiene
 * probabilidad: despejado es despejado.
 */
export interface CeldaTiempo {
  w: Weather;
  /** 0-100. Solo significa algo con `rain`/`thunder`. */
  p: number;
}

export const CELDA_DESPEJADA: CeldaTiempo = { w: 'clear', p: 0 };

const CLAMP_P = (n: number) => Math.max(0, Math.min(100, Math.round(n)));

/**
 * Lee una casilla guardada.
 *
 * <p>Formato: `"clear"` · `"rain"` / `"thunder"` (= 100%) · `"rain:70"` /
 * `"thunder:40"`. Se eligió texto y no un objeto `{w,p}` por tamaño: son 14 × 96
 * casillas en un solo documento, y un mapa por casilla lo multiplicaba por
 * cuatro sin dar nada a cambio.</p>
 *
 * <p><b>Lo de antes sigue valiendo</b>: un `"rain"` a secas —que es todo lo que
 * había guardado hasta el 2026-09-02— se lee como lluvia segura.</p>
 */
export function parseCelda(v: unknown): CeldaTiempo {
  if (typeof v !== 'string' || !v) return CELDA_DESPEJADA;
  const [w, p] = v.split(':');
  if (w !== 'rain' && w !== 'thunder') return CELDA_DESPEJADA;
  const n = p === undefined ? 100 : Number(p);
  return { w, p: Number.isFinite(n) ? CLAMP_P(n) : 100 };
}

/** Cómo se guarda una casilla (ver {@link parseCelda}). */
export function formatCelda(c: CeldaTiempo): string {
  if (c.w === 'clear' || c.p <= 0) return 'clear';
  return c.p >= 100 ? c.w : `${c.w}:${CLAMP_P(c.p)}`;
}

export const diaVacio = (): CeldaTiempo[] => Array.from({ length: SLOTS_DIA }, () => CELDA_DESPEJADA);

/**
 * El día tal y como está en Firestore → 96 casillas.
 *
 * <p><b>Convierte lo antiguo</b>: los días guardados con 24 casillas (una por
 * hora) se estiran a cuatro cada una. Sin esto, el pronóstico que ya hubiera
 * escrito el admin se leería como despejado de golpe.</p>
 */
export function diaDesdeFirestore(arr: unknown): CeldaTiempo[] {
  if (!Array.isArray(arr)) return diaVacio();
  if (arr.length === SLOTS_DIA) return arr.map(parseCelda);
  if (arr.length === 24) {
    const out: CeldaTiempo[] = [];
    for (const h of arr) {
      const c = parseCelda(h);
      for (let i = 0; i < SLOTS_HORA; i++) out.push(c);
    }
    return out;
  }
  return diaVacio();
}

/** ¿Este día viene en un formato que sabemos leer? (24 u 96 casillas) */
export const diaValido = (arr: unknown): boolean =>
  Array.isArray(arr) && (arr.length === SLOTS_DIA || arr.length === 24);

// ─── Reloj de Madrid ─────────────────────────────────────────────────────────

/** Fecha `YYYY-MM-DD` en zona de Madrid (la clave del pronóstico). */
export function madridDateStr(d: Date): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Madrid', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(d);
}

/**
 * Fecha, hora, minuto y CASILLA actuales en Madrid. Calcado del `madridNow()` de
 * `functions/index.js`, corrección del `24 → 0` incluida (algunos motores
 * devuelven 24 a medianoche).
 */
export function madridAhora(d: Date = new Date()): {
  date: string; hour: number; minute: number; slot: number;
} {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Madrid',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(d);
  const get = (t: string) => (parts.find((p) => p.type === t) || { value: '0' }).value;
  let hour = parseInt(get('hour'), 10);
  if (hour === 24) hour = 0;
  const minute = parseInt(get('minute'), 10) || 0;
  return {
    date: `${get('year')}-${get('month')}-${get('day')}`,
    hour,
    minute,
    slot: hour * SLOTS_HORA + Math.floor(minute / MINUTOS_SLOT),
  };
}

/** Casilla que le toca a una hora del reloj. */
export const slotDe = (hora: number, minuto = 0): number =>
  Math.max(0, Math.min(SLOTS_DIA - 1, hora * SLOTS_HORA + Math.floor(minuto / MINUTOS_SLOT)));

/** Casilla de una hora escrita `HH:MM` (la rejilla de los cuadros de horarios). */
export function slotDeHoraTexto(hhmm: string): number {
  const [h, m] = (hhmm || '').split(':').map((n) => parseInt(n, 10));
  return slotDe(isNaN(h) ? 0 : h, isNaN(m) ? 0 : m);
}

/** `HH:MM` de una casilla. */
export function etiquetaSlot(slot: number): string {
  const h = Math.floor(slot / SLOTS_HORA);
  const m = (slot % SLOTS_HORA) * MINUTOS_SLOT;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

// ─── ¿Llueve de verdad? ──────────────────────────────────────────────────────

/** Hash de 32 bits (FNV-1a + avalancha). Escrito con `Math.imul`/`>>>` para que
 *  dé EXACTAMENTE lo mismo aquí y en la copia de `functions/index.js`. */
function hash32(s: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  h ^= h >>> 16; h = Math.imul(h, 2246822507) >>> 0;
  h ^= h >>> 13; h = Math.imul(h, 3266489909) >>> 0;
  return (h ^ (h >>> 16)) >>> 0;
}

/**
 * El dado de un momento: un número 0-99 **estable**.
 *
 * <p><b>Por qué determinista y no un `Math.random()`</b>: el latido del mod pasa
 * cada ~15 s y volvería a tirar el dado cada vez, así que un 60% haría llover a
 * ratos de quince segundos. Además el servidor, la tarjeta del panel y este
 * cálculo tienen que decir lo mismo, y con un sorteo en vivo no coincidirían
 * nunca.</p>
 *
 * <p><b>Y por qué se tira una vez por HORA</b> (`slot/4`) y no por casilla: con un
 * dado por casilla, un 50% sería lluvia sí / lluvia no cada quince minutos, que
 * no se parece a nada. Con uno por hora, las cuatro casillas comparten el dado y
 * cada una lo compara con SU probabilidad — así una hora que va de 30% a 100%
 * empieza a llover a mitad, que es lo que se quiere poder programar.</p>
 */
export function sorteo(fecha: string, slot: number): number {
  return hash32(`${fecha}#${Math.floor(slot / SLOTS_HORA)}`) % 100;
}

/** El tiempo que se ve DE VERDAD en esa casilla, ya resuelta la probabilidad. */
export function climaResuelto(c: CeldaTiempo, fecha: string, slot: number): Weather {
  if (c.w === 'clear' || c.p <= 0) return 'clear';
  return sorteo(fecha, slot) < c.p ? c.w : 'clear';
}

// NOTA: `climaResuelto` se usa SOLO para «ahora mismo» (lo que hay en el cielo del
// juego en este instante). Lo que viene se enseña SIN resolver —icono de lo
// programado + su `%`—, que es lo que significa una previsión; ver la cabecera de
// `TiempoJartiland`. Por eso no hay un "horasResueltas": no hacía falta y era una
// forma cómoda de acabar enseñando certezas donde solo hay probabilidades.

// ─── Resúmenes por hora (para enseñar 96 casillas sin marear) ────────────────

/**
 * Lo que resume una hora: la casilla MÁS GORDA de sus cuatro (y, a igual tiempo,
 * la de más probabilidad). Es lo que se pinta cuando no cabe el cuarto de hora.
 */
export function resumenHora(dia: CeldaTiempo[], hora: number): CeldaTiempo {
  let peor: CeldaTiempo = CELDA_DESPEJADA;
  for (let i = 0; i < SLOTS_HORA; i++) {
    const c = dia[hora * SLOTS_HORA + i] || CELDA_DESPEJADA;
    const mejorTiempo = WEATHER_PESO[c.w] > WEATHER_PESO[peor.w];
    const mismoPeroMasProbable = WEATHER_PESO[c.w] === WEATHER_PESO[peor.w] && c.p > peor.p;
    if (mejorTiempo || mismoPeroMasProbable) peor = c;
  }
  return peor;
}

/** Las 24 horas resumidas (sin resolver el dado: es una PREVISIÓN, con su %). */
export const horasResumidas = (dia: CeldaTiempo[]): CeldaTiempo[] =>
  Array.from({ length: 24 }, (_, h) => resumenHora(dia, h));

/** Las 4 casillas de una hora. */
export const cuartosDeHora = (dia: CeldaTiempo[], hora: number): CeldaTiempo[] =>
  Array.from({ length: SLOTS_HORA }, (_, i) => dia[hora * SLOTS_HORA + i] || CELDA_DESPEJADA);

/** `"60 %"`, o `""` si no hay nada que decir (despejado, o lluvia segura). */
export const textoProbabilidad = (c: CeldaTiempo): string =>
  (c.w === 'clear' || c.p <= 0 || c.p >= 100 ? '' : `${c.p}%`);
